import React, { useEffect, useState } from "react";
import {
  Box,
  WixDesignSystemProvider,
  Page,
  Loader,
  Modal,
  CustomModalLayout,
  AnnouncementModalLayout,
  Text,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { dashboard } from "@wix/dashboard";
import { createClient } from "@wix/sdk";
import { embeddedScripts, appInstances } from "@wix/app-management";
import { siteProperties } from "@wix/business-tools";
import TrademarkRequestsList, { TrademarkRequest } from "./TrademarkRequestsList";
import CreateTrademarkRequest from "./CreateTrademarkRequest";
import ViewTrademarkRequest from "./ViewTrademarkRequest";

function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

type View = "list" | "create" | "edit" | "view";

function App() {
  const [client, setClient] = React.useState(null as any);
  const [currentView, setCurrentView] = useState<View>("list");
  const [selectedRequest, setSelectedRequest] = useState<TrademarkRequest | null>(null);
  const [currentUser] = useState<any>(null);
  const token = new URLSearchParams(window.location.search).get("token");
  const [instanceData, setInstanceData] = useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  } as any);

  // Modal states
  const [revertConfirmModalShown, setRevertConfirmModalShown] = useState(false);
  const [errorModalShown, setErrorModalShown] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [requestToRevert, setRequestToRevert] = useState<TrademarkRequest | null>(null);
  const [deleteConfirmModalShown, setDeleteConfirmModalShown] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const [pricingDisclosureModalShown, setPricingDisclosureModalShown] = useState(false);
  const [upgradeModalShown, setUpgradeModalShown] = useState(false);
  const [isMonitoringUpgrade, setIsMonitoringUpgrade] = useState(false);
  const initialPlanRef = React.useRef<string | undefined>(undefined);

  const API_BASE_URL = "/api";

  useEffect(() => {
    try {
      if (inIframe()) {
        const auth = dashboard.auth();
        console.log("=== DASHBOARD AUTH ===");
        console.log("Dashboard auth object:", auth);
        console.log("Dashboard auth keys:", Object.keys(auth || {}));
        console.log("Dashboard auth JSON:", JSON.stringify(auth, null, 2));
        
        var c = createClient({
          host: dashboard.host(),
          auth: auth,
          modules: {
            dashboard,
            embeddedScripts,
            appInstances,
            siteProperties,
          },
        });
        setClient(c);
      }
    } catch (error) {
      throw error;
    }
  }, []);

  // Function to reload instance data
  const reloadInstanceData = React.useCallback(async () => {
    if (client) {
      try {
        const data = await client.appInstances.getAppInstance();
        console.log("=== WIX INSTANCE DATA RELOADED ===");
        console.log("Full instance data:", JSON.stringify(data, null, 2));
        setInstanceData(data);
        return data;
      } catch (error: any) {
        console.error("Error getting app instance:", error);
        return null;
      }
    }
    return null;
  }, [client]);

  useEffect(() => {
    if (client) {
      reloadInstanceData();
    }
  }, [client, reloadInstanceData]);

  // Monitor for instance data changes after upgrade
  useEffect(() => {
    if (!isMonitoringUpgrade || !client) return;

    // Capture the initial plan when monitoring starts (only once)
    if (initialPlanRef.current === undefined) {
      initialPlanRef.current = instanceData?.instance?.billing?.packageName;
      console.log("Starting upgrade monitoring. Initial plan:", initialPlanRef.current);
    }

    const initialPlan = initialPlanRef.current;

    let pollInterval: NodeJS.Timeout;
    let focusHandler: () => void;
    let timeoutId: NodeJS.Timeout;

    // Poll every 5 seconds for up to 5 minutes
    const startTime = Date.now();
    const maxDuration = 5 * 60 * 1000; // 5 minutes
    const pollIntervalMs = 5000; // 5 seconds

    const poll = async () => {
      if (Date.now() - startTime > maxDuration) {
        console.log("Stopped polling for upgrade - max duration reached");
        setIsMonitoringUpgrade(false);
        return;
      }

      try {
        const data = await reloadInstanceData();
        if (data) {
          // Check if plan has changed (compare billing package name)
          const currentPlan = data?.instance?.billing?.packageName;
          
          if (currentPlan && currentPlan !== initialPlan) {
            console.log("Plan upgrade detected! Previous:", initialPlan, "Current:", currentPlan);
            setIsMonitoringUpgrade(false);
            // Refresh the page to show updated plan
            window.location.reload();
          }
        }
      } catch (error) {
        console.error("Error polling for upgrade:", error);
      }
    };

    // Start polling
    pollInterval = setInterval(poll, pollIntervalMs);

    // Also reload when window regains focus (user comes back from upgrade page)
    focusHandler = () => {
      console.log("Window focused - reloading instance data");
      reloadInstanceData();
    };
    window.addEventListener("focus", focusHandler);

    // Cleanup after max duration
    timeoutId = setTimeout(() => {
      setIsMonitoringUpgrade(false);
    }, maxDuration);

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("focus", focusHandler);
    };
  }, [isMonitoringUpgrade, client, reloadInstanceData, instanceData?.instance?.billing?.packageName]);

  // Reset ref when monitoring state changes
  useEffect(() => {
    if (!isMonitoringUpgrade) {
      initialPlanRef.current = undefined;
    }
  }, [isMonitoringUpgrade]);


  if (token) {
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text="Loading" />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  const handleCreateNew = () => {
    setSelectedRequest(null);
    setCurrentView("create");
  };

  const handleEdit = (request: TrademarkRequest) => {
    setSelectedRequest(request);
    setCurrentView("edit");
  };

  const handleView = (request: TrademarkRequest) => {
    setSelectedRequest(request);
    setCurrentView("view");
  };

  const handleSave = async (requestData: Partial<TrademarkRequest>) => {
    try {
      const instance = new URLSearchParams(window.location.search).get("instance");
      const isEdit = !!selectedRequest;
      const url = instance
        ? `${API_BASE_URL}/trademark-requests${isEdit ? `?_id=${selectedRequest._id}&instance=${encodeURIComponent(instance)}` : `?instance=${encodeURIComponent(instance)}`}`
        : `${API_BASE_URL}/trademark-requests${isEdit ? `?_id=${selectedRequest._id}` : ""}`;

      const method = isEdit ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...requestData,
          createdBy: currentUser?.id || "",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save trademark request");
      }

      setCurrentView("list");
      setSelectedRequest(null);
    } catch (error: any) {
      throw error;
    }
  };

  const handleCancel = () => {
    setCurrentView("list");
    setSelectedRequest(null);
  };

  const handleRevertToDraftClick = (request: TrademarkRequest) => {
    setRequestToRevert(request);
    setRevertConfirmModalShown(true);
  };

  const handleRevertToDraftConfirm = async () => {
    if (!requestToRevert) return;

    setRevertConfirmModalShown(false);
    
    try {
      const instance = new URLSearchParams(window.location.search).get("instance");
      const url = instance
        ? `${API_BASE_URL}/trademark-requests?_id=${requestToRevert._id}&instance=${encodeURIComponent(instance)}`
        : `${API_BASE_URL}/trademark-requests?_id=${requestToRevert._id}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...requestToRevert,
          status: "draft",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to revert to draft");
      }

      // Refresh the list view
      setCurrentView("list");
      setSelectedRequest(null);
      setRequestToRevert(null);
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to revert to draft");
      setErrorModalShown(true);
    }
  };

  const handleDeleteClick = (requestId: string) => {
    setRequestToDelete(requestId);
    setDeleteConfirmModalShown(true);
  };

  const handleDeleteConfirm = async () => {
    if (!requestToDelete) return;

    setDeleteConfirmModalShown(false);
    const requestId = requestToDelete;
    setRequestToDelete(null);

    try {
      const instance = new URLSearchParams(window.location.search).get("instance");
      const url = instance
        ? `${API_BASE_URL}/trademark-requests?_id=${requestId}&instance=${encodeURIComponent(instance)}`
        : `${API_BASE_URL}/trademark-requests?_id=${requestId}`;
      const response = await fetch(url, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      // Refresh the list view if we're on it
      if (currentView === "list") {
        window.location.reload();
      }
    } catch (err: any) {
      setErrorMessage(err.message || "Failed to delete trademark request");
      setErrorModalShown(true);
    }
  };

  const handleUpgradeClick = async () => {
    const APP_ID = "a6182f8d-d41e-4afc-81f9-19a4347cb5a5";
    
    // Try to get instance ID from instanceData first
    let instanceId =
      instanceData?.instance?.instanceId ||
      instanceData?.instanceId;

    // If not available, try to get it from the client
    if (!instanceId && client) {
      try {
        const appInstance = await client.appInstances.getAppInstance();
        instanceId = appInstance?.instance?.instanceId || appInstance?.instanceId;
        console.log("Got instanceId from client:", instanceId);
      } catch (error) {
        console.error("Error getting instance from client:", error);
      }
    }

    // Use APP_ID as appDefId (this is the app definition ID)
    const appDefId = APP_ID;

    console.log("Upgrade click - instanceData:", instanceData);
    console.log("Upgrade click - appDefId:", appDefId);
    console.log("Upgrade click - instanceId:", instanceId);

    if (appDefId && instanceId) {
      const upgradeUrl = `https://www.wix.com/apps/upgrade/${appDefId}?appInstanceId=${instanceId}`;
      console.log("Opening upgrade URL:", upgradeUrl);
      window.open(upgradeUrl, "_blank");
      setUpgradeModalShown(false);
      
      // Start monitoring for upgrade
      setIsMonitoringUpgrade(true);
      console.log("Started monitoring for upgrade...");
    } else {
      console.error("Cannot construct upgrade URL - missing appDefId or instanceId");
      setErrorMessage(
        "Unable to open upgrade page. Please try again or contact support."
      );
      setErrorModalShown(true);
      setUpgradeModalShown(false);
    }
  };

  // Helper functions for upgrade modal
  const getPlanAllowance = (planName?: string): number => {
    if (!planName) return 0;
    const planLower = planName.toLowerCase();
    if (planLower.includes("starter")) return 1;
    if (planLower.includes("portfolio")) return 2;
    if (planLower.includes("growth")) return 3;
    return 0;
  };

  const getCurrentPlan = (): string | undefined => {
    if (!instanceData?.instance) return undefined;
    const plan = instanceData.instance.billing?.packageName;
    return plan;
  };

  // Pricing Disclosure Content Component
  const PricingDisclosureContent: React.FC = () => {
    return (
      <Box direction="vertical" gap="SP4">
        <Text size="medium" weight="bold">
          Quick Reference: Subscription Plans
        </Text>
        <Box direction="vertical" gap="SP2">
          <Text size="small">
            <strong>Starter:</strong> $69/mo or <strong>$599/yr</strong> (Save
            27%) - 1 US filing/year, Basic knockout search, Standard support (3-5
            days)
          </Text>
          <Text size="small">
            <strong>Growth:</strong> $129/mo or <strong>$1,199/yr</strong> (Save
            22%) - 2 US filings/year, Comprehensive search, Priority support (1-2
            days) + 1 call
          </Text>
          <Text size="small">
            <strong>Portfolio:</strong> $189/mo or <strong>$1,799/yr</strong>{" "}
            (Save 21%) - 3 US filings/year, Comprehensive search + monitoring,
            Priority support (1-2 days) + 2 calls
          </Text>
        </Box>
        <Text size="small" secondary>
          <strong>
            All prices INCLUDE USPTO government filing fees ($350 per class).
          </strong>
        </Text>

        <Box direction="vertical" gap="SP3" marginTop="SP4">
          <Text size="medium" weight="bold">
            What Each Plan Covers
          </Text>

          <Box direction="vertical" gap="SP2">
            <Text size="small" weight="bold">
              Starter – $599/year (or $69/month)
            </Text>
            <Text size="small">
              <strong>Included:</strong> 1 US trademark filing per calendar year
              (1 class; additional classes +$549 each), Basic knockout trademark
              search, Real-time status dashboard, Email support (3–5 business
              days), Renewal reminders, Non-substantive Office Action responses
            </Text>
            <Text size="small">
              <strong>Not Included:</strong> Strategy calls (can purchase at $99
              each), Substantive Office Action responses (start at $249),
              Maintenance filings (available à la carte), International filings
            </Text>
          </Box>

          <Box direction="vertical" gap="SP2">
            <Text size="small" weight="bold">
              Growth – $1,199/year (or $129/month)
            </Text>
            <Text size="small">
              <strong>Included:</strong> 2 US trademark filings per calendar year
              (1 class each), Comprehensive trademark search, Real-time status
              dashboard, Priority email support (1–2 business days), 1 strategy
              call per year (30 min), Renewal reminders, Non-substantive Office
              Action responses
            </Text>
            <Text size="small">
              <strong>Not Included:</strong> Substantive Office Action responses
              (start at $249), Maintenance filings (available discounted at $199
              service fee), International filings
            </Text>
          </Box>

          <Box direction="vertical" gap="SP2">
            <Text size="small" weight="bold">
              Portfolio – $1,799/year (or $189/month)
            </Text>
            <Text size="small">
              <strong>Included:</strong> 3 US trademark filings per calendar year
              (1 class each), Comprehensive trademark search + quarterly brand
              monitoring, Real-time status dashboard, Priority email support (1–2
              business days), 2 strategy calls per year (30 min each), Renewal
              reminders, Non-substantive Office Action responses, Discounted
              maintenance filing service
            </Text>
            <Text size="small">
              <strong>Not Included:</strong> Substantive Office Action responses
              (quoted case-by-case), International filings (custom quotes
              available)
            </Text>
          </Box>
        </Box>

        <Box direction="vertical" gap="SP3" marginTop="SP4">
          <Text size="medium" weight="bold">
            À La Carte Add-Ons (All Plans)
          </Text>
          <Box direction="vertical" gap="SP1">
            <Text size="small">
              • <strong>Trademark Search (basic):</strong> $99 - Basic knockout
              search without filing
            </Text>
            <Text size="small">
              • <strong>Trademark Search (comprehensive):</strong> $199 - Full
              search with state & common law checks
            </Text>
            <Text size="small">
              • <strong>Extra Filing:</strong> $299–$329* - Full filing with USPTO
              fee + support
            </Text>
            <Text size="small">
              • <strong>Extra Trademark Class:</strong> $549 - $199 service + $350
              government fee
            </Text>
            <Text size="small">
              • <strong>Strategy Call (Starter only):</strong> $99 - 30-minute
              consultation
            </Text>
            <Text size="small">
              • <strong>Office Action Response (substantive):</strong> $249+ -
              Response to substantive USPTO refusal
            </Text>
            <Text size="small">
              • <strong>Section 8 Maintenance Filing:</strong> $574 - $249 service
              + $325 government fee (5–6 year proof of use)
            </Text>
            <Text size="small">
              • <strong>Section 9 Renewal Filing:</strong> $424 - $99 service +
              $325 government fee (10-year renewal)
            </Text>
            <Text size="small">
              • <strong>Combined Section 8 + 9:</strong> $648 - Both maintenance &
              renewal filings
            </Text>
          </Box>
          <Text size="small" secondary>
            *Extra filing cost depends on plan: Starter $299, Growth $329,
            Portfolio $299
          </Text>
        </Box>

        <Box direction="vertical" gap="SP3" marginTop="SP4">
          <Text size="medium" weight="bold">
            Refund & Cancellation Policy
          </Text>
          <Box direction="vertical" gap="SP2">
            <Text size="small" weight="bold">
              7-Day Money-Back Guarantee
            </Text>
            <Text size="small">
              • If you cancel within 7 days <strong>without</strong> using any
              service, you're eligible for a full refund
            </Text>
            <Text size="small">
              • After 7 days: Subscriptions are non-refundable
            </Text>
            <Text size="small">
              • Once application is filed with USPTO: All fees are non-refundable
              (per USPTO policy)
            </Text>
          </Box>
          <Box direction="vertical" gap="SP2">
            <Text size="small" weight="bold">
              Monthly Subscription Cancellation
            </Text>
            <Text size="small">
              • Cancel anytime, effective next billing cycle
            </Text>
            <Text size="small">• No early termination fee</Text>
          </Box>
          <Box direction="vertical" gap="SP2">
            <Text size="small" weight="bold">
              Annual Subscription Cancellation
            </Text>
            <Text size="small">• Can cancel at the next renewal date</Text>
            <Text size="small">• You keep the annual payment (no refunds)</Text>
          </Box>
        </Box>

        <Box direction="vertical" gap="SP3" marginTop="SP4">
          <Text size="medium" weight="bold">
            Yearly vs. Monthly Savings
          </Text>
          <Box direction="vertical" gap="SP1">
            <Text size="small">
              • <strong>Starter:</strong> $69 × 12 = $828 vs.{" "}
              <strong>$599/year</strong> - Save $229/year (28% off)
            </Text>
            <Text size="small">
              • <strong>Growth:</strong> $129 × 12 = $1,548 vs.{" "}
              <strong>$1,199/year</strong> - Save $349/year (23% off)
            </Text>
            <Text size="small">
              • <strong>Portfolio:</strong> $189 × 12 = $2,268 vs.{" "}
              <strong>$1,799/year</strong> - Save $469/year (21% off)
            </Text>
          </Box>
        </Box>

        <Box direction="vertical" gap="SP3" marginTop="SP4">
          <Text size="medium" weight="bold">
            Timeline & When You'll Pay Extra
          </Text>
          <Box direction="vertical" gap="SP2">
            <Text size="small">
              <strong>Year 0–1 (During Your Subscription):</strong> Subscription
              cost: $599–$1,799 (annual), USPTO government fees: INCLUDED, Search:
              INCLUDED, Support: INCLUDED
            </Text>
            <Text size="small">
              <strong>Year 5–6 (Maintenance Reminder):</strong> Section 8
              Declaration filing: $574 ($249 service + $325 government) - Optional
              but highly recommended
            </Text>
            <Text size="small">
              <strong>Year 10 (Renewal):</strong> Section 9 Renewal filing: $424
              ($99 service + $325 government) - Must file to keep your trademark
              active
            </Text>
          </Box>
        </Box>

        <Box direction="vertical" gap="SP3" marginTop="SP4">
          <Text size="medium" weight="bold">
            Additional Disclosures
          </Text>
          <Box direction="vertical" gap="SP2">
            <Text size="small">
              <strong>What Information Becomes Public?</strong> Trademark
              applications are public record. Your name, address, mark
              description, and goods/services are visible in the USPTO database.
            </Text>
            <Text size="small">
              <strong>Liability Disclaimer:</strong> We provide trademark
              application support, not legal advice. For legal counsel on
              infringement, enforcement, or disputes, consult a licensed trademark
              attorney.
            </Text>
            <Text size="small">
              <strong>Non-Refundable After Filing:</strong> Once application is
              submitted to the USPTO, all fees are non-refundable per USPTO
              policy.
            </Text>
            <Text size="small">
              <strong>Your Ownership:</strong>{" "}
              <strong>You own the trademark.</strong> We are merely your
              agent/representative. The mark is registered in your name (or your
              entity's name).
            </Text>
          </Box>
        </Box>
      </Box>
    );
  };

  // Render main content
  let mainContent;
  if (currentView === "create" || currentView === "edit") {
    mainContent = (
      <CreateTrademarkRequest
        onSave={handleSave}
        onCancel={handleCancel}
        initialData={selectedRequest || undefined}
        isEdit={currentView === "edit"}
        instanceData={instanceData}
        client={client}
      />
    );
  } else if (currentView === "view" && selectedRequest) {
    mainContent = (
      <ViewTrademarkRequest
        request={selectedRequest}
        onEdit={() => handleEdit(selectedRequest)}
        onBack={handleCancel}
        onRevertToDraft={() => handleRevertToDraftClick(selectedRequest)}
      />
    );
  } else {
    mainContent = (
      <TrademarkRequestsList
        onCreateNew={handleCreateNew}
        onEdit={handleEdit}
        onView={handleView}
        instanceData={instanceData}
        onDeleteClick={handleDeleteClick}
        onPricingDisclosureClick={() => setPricingDisclosureModalShown(true)}
        onUpgradeClick={() => setUpgradeModalShown(true)}
      />
    );
  }

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      {mainContent}
      {/* Revert Confirmation Modal */}
      <Modal
        isOpen={revertConfirmModalShown}
        onRequestClose={() => {
          setRevertConfirmModalShown(false);
          setRequestToRevert(null);
        }}
        shouldCloseOnOverlayClick
        screen="desktop"
      >
        <CustomModalLayout
          primaryButtonText="Revert to Draft"
          primaryButtonOnClick={handleRevertToDraftConfirm}
          secondaryButtonText="Cancel"
          secondaryButtonOnClick={() => {
            setRevertConfirmModalShown(false);
            setRequestToRevert(null);
          }}
          onCloseButtonClick={() => {
            setRevertConfirmModalShown(false);
            setRequestToRevert(null);
          }}
          title="Revert to Draft"
          content={
            <Text>
              Are you sure you want to revert "{requestToRevert?.trademarkName}" back to draft? This will allow you to edit it again.
            </Text>
          }
        />
      </Modal>
      {/* Error Modal */}
      <Modal
        isOpen={errorModalShown}
        onRequestClose={() => setErrorModalShown(false)}
        shouldCloseOnOverlayClick
        screen="desktop"
      >
        <CustomModalLayout
          theme="destructive"
          primaryButtonText="OK"
          primaryButtonOnClick={() => setErrorModalShown(false)}
          onCloseButtonClick={() => setErrorModalShown(false)}
          title="Error"
          content={<Text>{errorMessage}</Text>}
        />
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmModalShown}
        onRequestClose={() => {
          setDeleteConfirmModalShown(false);
          setRequestToDelete(null);
        }}
        shouldCloseOnOverlayClick
        screen="desktop"
      >
        <CustomModalLayout
          theme="destructive"
          primaryButtonText="Delete"
          primaryButtonOnClick={handleDeleteConfirm}
          secondaryButtonText="Cancel"
          secondaryButtonOnClick={() => {
            setDeleteConfirmModalShown(false);
            setRequestToDelete(null);
          }}
          onCloseButtonClick={() => {
            setDeleteConfirmModalShown(false);
            setRequestToDelete(null);
          }}
          title="Delete Trademark Request"
          content={
            <Text>
              Are you sure you want to delete this trademark request? This
              action cannot be undone.
            </Text>
          }
        />
      </Modal>
      {/* Pricing Disclosure Modal */}
      <Modal
        isOpen={pricingDisclosureModalShown}
        onRequestClose={() => setPricingDisclosureModalShown(false)}
        shouldCloseOnOverlayClick
        screen="desktop"
      >
        <CustomModalLayout
          primaryButtonText="Close"
          primaryButtonOnClick={() => setPricingDisclosureModalShown(false)}
          onCloseButtonClick={() => setPricingDisclosureModalShown(false)}
          title="Complete Pricing & Fee Disclosure"
          content={
            <Box padding="SP6" maxHeight="70vh" style={{ overflowY: "auto" }}>
              <PricingDisclosureContent />
            </Box>
          }
        />
      </Modal>
      {/* Upgrade Modal */}
      <Modal
        isOpen={upgradeModalShown}
        onRequestClose={() => setUpgradeModalShown(false)}
        shouldCloseOnOverlayClick
        screen="desktop"
      >
        <AnnouncementModalLayout
          skin="premium"
          title="Upgrade Your Plan"
          primaryButtonText="Upgrade"
          primaryButtonOnClick={handleUpgradeClick}
          secondaryButtonText="Cancel"
          secondaryButtonOnClick={() => setUpgradeModalShown(false)}
          onCloseButtonClick={() => setUpgradeModalShown(false)}
          linkText="Learn More"
          linkOnClick={() => {
            setPricingDisclosureModalShown(true);
            setUpgradeModalShown(false);
          }}
        >
          <Text>
            {(() => {
              const planName = getCurrentPlan();
              const allowance = getPlanAllowance(planName);

              if (!planName) {
                return "You need to upgrade to a plan to submit trademark requests. Choose from Starter (1 filing/year), Growth (2 filings/year), or Portfolio (3 filings/year).";
              } else {
                return `You've reached your plan's limit of ${allowance} submitted trademark request${
                  allowance > 1 ? "s" : ""
                } per year. Upgrade to a higher plan to submit more trademark requests.`;
              }
            })()}
          </Text>
        </AnnouncementModalLayout>
      </Modal>
    </WixDesignSystemProvider>
  );
}

export default App;
